import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import { normalizeSession } from '../utils/sessionUtils';
import { calculateTimetableSessions, normalizeReportSessionState } from '../utils/sessionPlanning';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import universityLogo from '../../assets/images/Benadir University Logo.png';
import {
  MdAssessment,
  MdSearch,
  MdFilterList,
  MdPeople,
  MdPercent,
  MdCalendarToday,
  MdSchool,
  MdAccessTime,
  MdCheckCircle,
  MdCancel,
  MdExpandMore,
  MdCheck,
  MdDownload
} from 'react-icons/md';

function TutorAttendanceReports() {
  const { getToken, user } = useAttendanceAuth();
  const location = useLocation();
  const selectedFacultyId = user?.faculty?.id || user?.faculty_id || '';
  const isFacultyReportView = location.pathname.includes('/faculty-admin/');
  const isFacultySubjectReportView = location.pathname.endsWith('/subject-report');
  const isFacultyBatchReportView = isFacultyReportView && !isFacultySubjectReportView;

  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState('');

  const [batches, setBatches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('');
  const departmentBatches = useMemo(
    () => batches.filter((batch) =>
      selectedDepartmentId && batch.departmentId === selectedDepartmentId
    ),
    [batches, selectedDepartmentId]
  );
  const [currentTimetables, setCurrentTimetables] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState('');
  const [selectedCampus, setSelectedCampus] = useState('');
  const [campusOptions, setCampusOptions] = useState([]);
  const availableCampusOptions = useMemo(() => {
    const options = new Map();
    buildTimetableCampusOptions(currentTimetables, selectedBatchId)
      .concat(campusOptions)
      .forEach((campus) => options.set(campus.value, campus));
    return Array.from(options.values());
  }, [currentTimetables, selectedBatchId, campusOptions]);
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [batchRoster, setBatchRoster] = useState([]);
  const [termInfo, setTermInfo] = useState(null);

  const [reportRows, setReportRows] = useState([]);
  const [totalWeeks, setTotalWeeks] = useState(0);
  const [subjectInfo, setSubjectInfo] = useState(null);
  const [subjectSessionStats, setSubjectSessionStats] = useState({
    total: 0,
    pending: 0,
    marked: 0,
    missed: 0
  });
  const [subjectSessionsAll, setSubjectSessionsAll] = useState([]);
  const [remainingSessionDetails, setRemainingSessionDetails] = useState([]);
  const [sessionDetailsData, setSessionDetailsData] = useState([]);
  const [sessionDetailsModal, setSessionDetailsModal] = useState({
    isOpen: false,
    title: '',
    sessions: []
  });
  const [studentAttendanceModal, setStudentAttendanceModal] = useState({
    isOpen: false,
    title: '',
    statusType: 'absent',
    studentName: '',
    studentId: '',
    faculty: '',
    department: '',
    batch: '',
    totalAbsent: 0,
    totalPresent: 0,
    records: []
  });
  const [facultyStudentModal, setFacultyStudentModal] = useState({
    isOpen: false,
    studentName: '',
    studentId: '',
    overallPercentage: null,
    subjectStats: []
  });

  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [isTodayFilterActive, setIsTodayFilterActive] = useState(false);
  const [showLowAttendanceOnly, setShowLowAttendanceOnly] = useState(false);
  const [abovePercentage, setAbovePercentage] = useState('');
  const [belowPercentage, setBelowPercentage] = useState('');
  const selectorRequestRef = useRef(0);
  const subjectOptionsRequestRef = useRef(0);
  const reportRequestRef = useRef(0);
  const campusRequestRef = useRef(0);
  const percentageOptions = useMemo(
    () => Array.from({ length: 11 }, (_, index) => String(index * 10)),
    []
  );

  useEffect(() => {
    // Clear role-specific selector state immediately to avoid stale UI
    reportRequestRef.current += 1;
    campusRequestRef.current += 1;
    setError('');
    setSelectedBatchId('');
    setSelectedCampus('');
    setCampusOptions([]);
    setSelectedSubjectId('');
    setSelectedDepartmentId('');
    setDepartments([]);
    setBatches([]);
    setCurrentTimetables([]);
    setSubjects([]);
    setBatchRoster([]);
    setTermInfo(null);
    setReportRows([]);
    setTotalWeeks(0);
    setSubjectInfo(null);
    setSubjectSessionStats({ total: 0, pending: 0, marked: 0, missed: 0 });
    setSubjectSessionsAll([]);
    setRemainingSessionDetails([]);
    setSessionDetailsData([]);

    // Reload selector options whenever route/role context changes
    const requestId = selectorRequestRef.current + 1;
    selectorRequestRef.current = requestId;
    loadReportSelectors(requestId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFacultyReportView, location.pathname, selectedFacultyId]);

  const fetchReportSessions = async (token) => {
    if (isFacultyReportView) {
      if (!selectedBatchId) return { sessions: [], teachingWeeks: [] };
      const params = { batch_id: Number(selectedBatchId) };
      if (isFacultySubjectReportView) params.include_future = true;
      if (selectedCampus && selectedCampus !== '__unassigned__') params.campus = selectedCampus;
      const facultyResult = await attendanceAPI.getSessions(params, token);
      return {
        sessions: selectedCampus === '__unassigned__' ? [] : (facultyResult?.sessions || []).map((session) => normalizeReportSessionState({
          ...session,
          ...normalizeSession(session)
        })),
        teachingWeeks: facultyResult?.teaching_weeks || []
      };
    }

    const historyResult = await attendanceAPI.getHistory({ report_mode: true }, token);
    if (!historyResult?.success) throw new Error(historyResult?.message || 'Failed to load tutor report');
    if (!Array.isArray(historyResult.report_subjects) || !Array.isArray(historyResult.timetables)) {
      throw new Error('The attendance service has not loaded the current tutor report update. Restart local Odoo and reload this page.');
    }
    return {
      sessions: (historyResult?.sessions || []).map((session) => normalizeReportSessionState({
        ...session,
        ...normalizeSession(session)
      })),
      teachingWeeks: historyResult?.teaching_weeks || [],
      subjectReports: historyResult?.report_subjects || [],
      timetables: historyResult?.timetables || []
    };
  };

  useEffect(() => {
    const requestId = ++campusRequestRef.current;
    if (!isFacultyReportView || !selectedBatchId) {
      setCampusOptions([]);
      return;
    }
    attendanceAPI.getBatchStudents(selectedBatchId, getToken())
      .then((result) => {
        if (requestId === campusRequestRef.current) {
          setCampusOptions(result?.campus_options || []);
        }
      })
      .catch((err) => {
        if (requestId === campusRequestRef.current) {
          setError(err.message || 'Failed to load campus options');
          setCampusOptions([]);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFacultyReportView, selectedBatchId]);

  useEffect(() => {
    if (!isFacultySubjectReportView || !selectedBatchId) return;
    const requestId = ++subjectOptionsRequestRef.current;
    const timetableSubjects = buildTimetableSubjectOptions(currentTimetables, selectedBatchId);
    setSubjects(timetableSubjects);

    const params = { batch_id: Number(selectedBatchId) };
    attendanceAPI.getSessions(params, getToken())
      .then((result) => {
        if (requestId !== subjectOptionsRequestRef.current) return;
        if (!result?.success) throw new Error(result?.message || 'Failed to load subjects');
        setSubjects(mergeSubjectOptions(
          timetableSubjects,
          buildSubjectOptions(result.sessions || [])
        ));
      })
      .catch((err) => {
        if (requestId === subjectOptionsRequestRef.current) {
          setError(err.message || 'Failed to load subjects');
        }
      });
    return () => { subjectOptionsRequestRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFacultySubjectReportView, selectedBatchId, currentTimetables]);

  const loadReportSelectors = async (requestId = selectorRequestRef.current) => {
    try {
      setLoadingSubjects(true);
      setError('');
      const token = getToken();

      if (isFacultyReportView) {
        const [departmentsResult, batchesResult, timetablesResult] = await Promise.all([
          attendanceAPI.getDepartments({}, token),
          attendanceAPI.getBatches({}, token),
          attendanceAPI.getTimetables({ state: 'active' }, token).catch(() => ({ success: false, timetables: [] }))
        ]);
        if (requestId !== selectorRequestRef.current) return;

        const activeTimetables = (timetablesResult?.timetables || []).filter(
          (item) => String(item?.state || '').toLowerCase() === 'active'
        );
        setCurrentTimetables(activeTimetables);

        if (!departmentsResult?.success || !batchesResult?.success) {
          throw new Error(departmentsResult?.message || batchesResult?.message || 'Failed to load report filters');
        }

        setDepartments((departmentsResult.departments || []).map((department) => ({
          id: String(department.id),
          name: department.name || department.code || `Department ${department.id}`
        })).sort((a, b) => a.name.localeCompare(b.name)));

        const apiBatches = (batchesResult?.batches || []).map((batch) => ({
          id: String(batch?.id ?? batch?.batch_id ?? '').trim(),
          name: String(batch?.name ?? batch?.batch_name ?? batch?.code ?? '').trim(),
          departmentId: String(batch?.department_id ?? '').trim(),
          departmentName: String(batch?.department ?? '').trim()
        }));

        const availableBatches = apiBatches
          .filter((batch) => batch.id || batch.name)
          .map((batch) => ({
            id: batch.id || batch.name,
            name: batch.name || batch.id,
            departmentId: batch.departmentId,
            departmentName: batch.departmentName
          }))
          .sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );

        if (requestId !== selectorRequestRef.current) return;
        setBatches(availableBatches);
        setSubjects([]);
      } else {
        const result = await fetchReportSessions(token);
        const sessions = result.sessions.filter(Boolean);
        if (requestId !== selectorRequestRef.current) return;
        setCurrentTimetables(result.timetables);
        setSubjects(mergeSubjectOptions(
          buildSubjectOptions(sessions),
          result.subjectReports.map((subject) => ({ id: String(subject.id), name: subject.name }))
        ));
      }
    } catch (err) {
      if (requestId !== selectorRequestRef.current) return;
      console.error('Error loading report selectors:', err);
      setError(err.message || 'Failed to load report filters');
      setDepartments([]);
      setBatches([]);
      setCurrentTimetables([]);
      setSubjects([]);
    } finally {
      if (requestId !== selectorRequestRef.current) return;
      setLoadingSubjects(false);
    }
  };

  const loadSubjectReport = async (subjectId = '') => {
    const requestId = ++reportRequestRef.current;
    if ((!isFacultyReportView && !subjectId) ||
        (isFacultyReportView && (!selectedBatchId || (isFacultySubjectReportView && !subjectId)))) {
      setReportRows([]);
      setTotalWeeks(0);
      setSubjectInfo(null);
      setSubjectSessionStats({ total: 0, pending: 0, marked: 0, missed: 0 });
      setSubjectSessionsAll([]);
      setRemainingSessionDetails([]);
      setSessionDetailsData([]);
      setBatchRoster([]);
      setTermInfo(null);
      return;
    }

    try {
      setLoadingReport(true);
      setError('');
      const token = getToken();

      const [facultyRosterResult, sessionsResult] = await Promise.all([
        isFacultyReportView
          ? attendanceAPI.getBatchStudents(selectedBatchId, token, selectedCampus)
          : Promise.resolve(null),
        fetchReportSessions(token)
      ]);
      if (requestId !== reportRequestRef.current) return;
      const rosterResult = facultyRosterResult || sessionsResult.subjectReports?.find(
        (subject) => String(subject.id) === String(subjectId)
      ) || null;
      if (!isFacultyReportView && !rosterResult) {
        throw new Error('This subject has no current-term tutor assignment. Refresh the subject list and check the active timetable assignment in Odoo.');
      }
      const allSessions = sessionsResult.sessions;
      setTotalWeeks(sessionsResult.teachingWeeks.length);
      const batchScopedSessions = allSessions.filter(
        (session) =>
          !isFacultyReportView ||
          ((!rosterResult?.start_date || getSessionDateKeyFromSession(session) >= rosterResult.start_date) &&
            (!rosterResult?.end_date || getSessionDateKeyFromSession(session) <= rosterResult.end_date))
      );
      setBatchRoster(rosterResult?.students || []);
      setTermInfo(rosterResult);
      const allSubjectSessions = batchScopedSessions.filter(
        (session) =>
          session &&
          (session.state || session.status || '').toLowerCase() !== 'cancelled' &&
          (!subjectId ||
            String(getSessionSubjectId(session) || '') === String(subjectId) ||
            (!isFacultyReportView && session.subjects?.some((subject) => String(subject.id) === String(subjectId))))
      );
      const subjectSessions = allSubjectSessions.filter(
        (session) => (session.state || '').toLowerCase() === 'marked'
      );
      setSubjectSessionsAll(allSubjectSessions);

      const markedCount = allSubjectSessions.filter(
        (session) => (session.state || session.status || '').toLowerCase() === 'marked'
      ).length;
      const missedCount = allSubjectSessions.filter(
        (session) => (session.state || session.status || '').toLowerCase() === 'missed'
      ).length;

      const subjectPlannedSlots = !isFacultyBatchReportView
        ? (isFacultyReportView ? currentTimetables : sessionsResult.timetables || [])
          .filter((timetable) =>
            !isFacultyReportView || (
              extractTimetableBatches(timetable).some((batch) => String(batch.id) === String(selectedBatchId)) &&
              (!selectedCampus || timetable.campus === selectedCampus)))
          .flatMap((timetable) => calculateTimetableSessions(timetable))
          .filter((subject) => String(subject.subjectId) === String(subjectId))
          .flatMap((subject) => subject.slots)
          .filter((slot) =>
            (!rosterResult?.start_date || slot.session_date >= rosterResult.start_date) &&
            (!rosterResult?.end_date || slot.session_date <= rosterResult.end_date))
        : [];
      // A shared lesson can appear in multiple batch timetables for the same tutor.
      const plannedSlots = isFacultyReportView ? subjectPlannedSlots : Array.from(new Map(
        subjectPlannedSlots.map((slot) => [
          `${slot.session_date}|${slot.time_start}|${slot.time_end}|${slot.campus || ''}`, slot
        ])
      ).values());
      const plannedTotal = plannedSlots.length;
      const recordedTotal = markedCount + missedCount;
      const total = !isFacultyBatchReportView && plannedTotal
        ? Math.max(plannedTotal, recordedTotal)
        : allSubjectSessions.length;
      const pendingCount = !isFacultyBatchReportView && plannedTotal
        ? Math.max(total - recordedTotal, 0)
        : allSubjectSessions.filter(
          (session) => (session.state || session.status || '').toLowerCase() === 'pending'
        ).length;

      const recordedKeys = new Set(allSubjectSessions
        .filter((session) => ['marked', 'missed'].includes((session.state || session.status || '').toLowerCase()))
        .map((session) => `${getSessionDateKeyFromSession(session)}|${session.period_id}|${session.campus || ''}`));
      const remainingSlots = plannedSlots
        .filter((slot) => !recordedKeys.has(`${slot.session_date}|${slot.period_id}|${slot.campus || ''}`))
        .slice(0, pendingCount);
      setRemainingSessionDetails(remainingSlots.length === pendingCount
        ? remainingSlots
        : allSubjectSessions.filter((session) =>
          (session.state || session.status || '').toLowerCase() === 'pending').slice(0, pendingCount));

      setSubjectSessionStats({
        total,
        pending: pendingCount,
        marked: markedCount,
        missed: missedCount
      });

      const primarySession = subjectSessions[0] || null;

      const sessionDetails = await Promise.all(
        subjectSessions.map((session) =>
          fetchSessionAttendanceDetail(session.id, token, isFacultyReportView)
        )
      );
      if (requestId !== reportRequestRef.current) return;
      setSessionDetailsData(sessionDetails);

      const detailInfo = extractSubjectInfoFromViewDetails(sessionDetails, primarySession);
      setSubjectInfo({
        ...detailInfo,
        faculty: detailInfo?.faculty || rosterResult?.faculty || '',
        department: detailInfo?.department || rosterResult?.department || '',
        batch: detailInfo?.batch || rosterResult?.batch || ''
      });

      const studentMap = new Map();

      sessionDetails.forEach((detail) => {
        const students = detail?.students || [];
        students.forEach((student) => {
          const studentKey = String(student.student_id || student.id || '');
          if (!studentKey) return;

          const existing = studentMap.get(studentKey) || {
            studentId: getDisplayStudentId(student),
            studentName: cleanStudentName(student.student_name || student.name || 'Unknown Student'),
            present: 0
          };

          if (student.is_present) {
            existing.present += 1;
          }

          if (
            existing.studentName === 'Unknown Student' &&
            (student.student_name || student.name)
          ) {
            existing.studentName = cleanStudentName(student.student_name || student.name);
          }

          const bestStudentId = getDisplayStudentId(student);
          if (bestStudentId && !looksLikePlainNumericId(bestStudentId)) {
            existing.studentId = bestStudentId;
          }

          studentMap.set(studentKey, existing);
        });
      });

      const rows = Array.from(studentMap.values()).map((student) => {
        const absent = Math.max(totalSessionsSafe(subjectSessions.length) - student.present, 0);
        const percentage =
          subjectSessions.length > 0
            ? Math.round((student.present / subjectSessions.length) * 100)
            : 0;

        return {
          ...student,
          totalAttendance: subjectSessions.length,
          absent,
          percentage
        };
      });

      rows.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));
      setReportRows(rows);
    } catch (err) {
      if (requestId !== reportRequestRef.current) return;
      console.error('Error loading subject report:', err);
      setError(err.message || 'Failed to load attendance report');
      setReportRows([]);
      setTotalWeeks(0);
      setSubjectSessionStats({ total: 0, pending: 0, marked: 0, missed: 0 });
      setSubjectSessionsAll([]);
      setSessionDetailsData([]);
      setBatchRoster([]);
      setTermInfo(null);
    } finally {
      if (requestId === reportRequestRef.current) setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (isFacultyReportView) {
      if (!selectedBatchId || (isFacultySubjectReportView && !selectedSubjectId)) return;
      loadSubjectReport(selectedSubjectId);
      return;
    }
    if (!selectedSubjectId) return;
    loadSubjectReport(selectedSubjectId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedBatchId, selectedCampus, selectedSubjectId, isFacultyReportView, isFacultySubjectReportView]);

  const attendanceRows = useMemo(() => {
    return buildAttendanceRows(
      sessionDetailsData, subjectSessionsAll, fromDate, toDate,
      isFacultyReportView || termInfo ? batchRoster : null
    );
  }, [sessionDetailsData, subjectSessionsAll, fromDate, toDate, isFacultyReportView, batchRoster, termInfo]);
  const hasDateFilter = useMemo(() => {
    const dateRange = normalizeDateRange(fromDate, toDate);
    return Boolean(dateRange.from || dateRange.to);
  }, [fromDate, toDate]);

  const averageAttendance = useMemo(() => {
    if (isFacultyReportView || termInfo) {
      if (attendanceRows.length === 0) return null;
      const totalPresentAcrossMarked = attendanceRows.reduce(
        (acc, row) => acc + Number(row.present || 0),
        0
      );
      const totalPossibleAttendance = attendanceRows.reduce(
        (acc, row) => acc + Number(row.totalAttendance || 0),
        0
      );
      if (totalPossibleAttendance <= 0) return null;
      return Math.round((totalPresentAcrossMarked / totalPossibleAttendance) * 100);
    }

    if (attendanceRows.length === 0) return 0;
    const sum = attendanceRows.reduce((acc, row) => acc + row.percentage, 0);
    return Math.round(sum / attendanceRows.length);
  }, [attendanceRows, isFacultyReportView, termInfo]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const aboveValue = parsePercentageSelection(abovePercentage);
    const belowValue = parsePercentageSelection(belowPercentage);

    return attendanceRows.filter((row) => {
      const matchesSearch =
        !query ||
        row.studentName.toLowerCase().includes(query) ||
        row.studentId.toLowerCase().includes(query);

      const matchesLowAttendance = !showLowAttendanceOnly || (row.percentage !== null && row.percentage < 75);
      const matchesAbove = aboveValue === null || (row.percentage !== null && row.percentage > aboveValue);
      const matchesBelow = belowValue === null || (row.percentage !== null && row.percentage < belowValue);

      return matchesSearch && matchesLowAttendance && matchesAbove && matchesBelow;
    });
  }, [attendanceRows, search, showLowAttendanceOnly, abovePercentage, belowPercentage]);
  const visibleRows = useMemo(() => {
    // Date-filtered subject reports list recorded absences; batch reports keep the roster.
    if (!hasDateFilter || isFacultyBatchReportView) return filteredRows;
    return filteredRows.filter((row) => row.absent > 0);
  }, [filteredRows, hasDateFilter, isFacultyBatchReportView]);
  const totalStudents = attendanceRows.length;
  const selectedSubjectName = useMemo(
    () =>
      subjects.find((item) => String(item.id) === String(selectedSubjectId))?.name ||
      (isFacultyReportView ? 'All Subjects' : 'N/A'),
    [subjects, selectedSubjectId, isFacultyReportView]
  );
  const selectedBatchName = useMemo(() => {
    if (!selectedBatchId) return '';
    const matchedBatch = batches.find(
      (batch) =>
        String(batch.id || '').trim() === String(selectedBatchId).trim() ||
        String(batch.name || '').trim() === String(selectedBatchId).trim()
    );
    return matchedBatch?.name || '';
  }, [batches, selectedBatchId]);
  const sessionByIdMap = useMemo(() => {
    const map = new Map();
    subjectSessionsAll.forEach((session) => {
      if (!session) return;
      const key = String(session.id || session.session_id || session.sessionId || '');
      if (!key) return;
      map.set(key, session);
    });
    return map;
  }, [subjectSessionsAll]);
  const facultyBatchMatrix = useMemo(() => {
    if (!isFacultyReportView) {
      return { subjects: [], rows: [] };
    }

    const subjects = [];
    const subjectMap = new Map();
    const sessionsInDateRange = filterSessionsByDateRange(
      subjectSessionsAll, normalizeDateRange(fromDate, toDate)
    );
    const includedSessionIds = new Set(
      sessionsInDateRange.map((session) => String(session.id || session.session_id || session.sessionId || ''))
    );

    sessionsInDateRange.forEach((session) => {
      if (!session) return;
      const subjectKey = getSubjectKey(session);
      if (!subjectKey) return;
      if (!subjectMap.has(subjectKey)) {
        const subjectLabel = getSubjectLabel(session);
        subjectMap.set(subjectKey, subjectLabel);
        subjects.push({ key: subjectKey, label: subjectLabel });
      }
    });

    const presentByStudent = new Map();
    const markedByStudent = new Map();
    sessionDetailsData.forEach((detail) => {
      const sessionId = String(getSessionIdFromDetail(detail) || '');
      if (!sessionId || !includedSessionIds.has(sessionId)) return;
      const session = sessionByIdMap.get(sessionId);
      if (!session) return;
      const state = String(session.state || session.status || '').toLowerCase();
      if (state !== 'marked') return;
      const subjectKey = getSubjectKey(session);
      if (!subjectKey) return;

      const students = Array.isArray(detail?.students) ? detail.students : [];
      students.forEach((student) => {
        const studentKey = String(student?.student_id || student?.id || '');
        if (!studentKey) return;
        if (!markedByStudent.has(studentKey)) {
          markedByStudent.set(studentKey, new Map());
        }
        const perSubjectMarked = markedByStudent.get(studentKey);
        perSubjectMarked.set(subjectKey, (perSubjectMarked.get(subjectKey) || 0) + 1);
        if (!student.is_present) return;
        if (!presentByStudent.has(studentKey)) {
          presentByStudent.set(studentKey, new Map());
        }
        const perSubject = presentByStudent.get(studentKey);
        perSubject.set(subjectKey, (perSubject.get(subjectKey) || 0) + 1);
      });
    });

    const rows = attendanceRows.map((studentRow) => {
      const perSubjectPresent = presentByStudent.get(String(studentRow.studentKey || '')) || new Map();
      const perSubjectMarked = markedByStudent.get(String(studentRow.studentKey || '')) || new Map();
      const subjectPercentages = {};
      const subjectCounts = {};
      let totalPresent = 0;
      let totalPossible = 0;

      subjects.forEach((subject) => {
        const denominator = perSubjectMarked.get(subject.key) || 0;
        const present = perSubjectPresent.get(subject.key) || 0;
        if (denominator > 0) {
          const percentage = Math.round((present / denominator) * 100);
          const absent = Math.max(denominator - present, 0);
          subjectPercentages[subject.key] = percentage;
          subjectCounts[subject.key] = { present, absent, percentage };
          totalPresent += present;
          totalPossible += denominator;
        } else {
          subjectPercentages[subject.key] = null;
          subjectCounts[subject.key] = { present: 0, absent: 0, percentage: null };
        }
      });

      const overallPercentage = totalPossible > 0 ? Math.round((totalPresent / totalPossible) * 100) : null;

      return {
        ...studentRow,
        subjectPercentages,
        subjectCounts,
        overallPercentage
      };
    });

    return { subjects, rows };
  }, [isFacultyReportView, subjectSessionsAll, sessionDetailsData, sessionByIdMap, attendanceRows, fromDate, toDate]);
  const facultyVisibleRows = useMemo(() => {
    if (!isFacultyReportView) return [];
    const query = search.trim().toLowerCase();
    const aboveValue = parsePercentageSelection(abovePercentage);
    const belowValue = parsePercentageSelection(belowPercentage);

    return facultyBatchMatrix.rows.filter((row) => {
      const matchesSearch =
        !query ||
        String(row.studentName || '').toLowerCase().includes(query) ||
        String(row.studentId || '').toLowerCase().includes(query);
      const percentage = row.overallPercentage;
      const matchesLowAttendance = !showLowAttendanceOnly || (percentage !== null && percentage < 75);
      const matchesAbove = aboveValue === null || (percentage !== null && percentage > aboveValue);
      const matchesBelow = belowValue === null || (percentage !== null && percentage < belowValue);
      return matchesSearch && matchesLowAttendance && matchesAbove && matchesBelow;
    });
  }, [
    isFacultyReportView,
    facultyBatchMatrix.rows,
    search,
    abovePercentage,
    belowPercentage,
    showLowAttendanceOnly
  ]);
  const useFacultyBatchMatrixView = isFacultyBatchReportView;
  const activeVisibleRows = useFacultyBatchMatrixView ? facultyVisibleRows : visibleRows;

  const handleSubjectChange = (event) => {
    const value = event.target.value;
    setRemainingSessionDetails([]);
    setSelectedSubjectId(value);
  };

  const selectBatch = (value) => {
    reportRequestRef.current += 1;
    setBatchRoster([]);
    setTermInfo(null);
    setCampusOptions([]);
    setSelectedBatchId(value);
    setSelectedCampus('');
    setSelectedSubjectId('');
    setSubjects(buildTimetableSubjectOptions(currentTimetables, value));
    setSubjectSessionsAll([]);
    setRemainingSessionDetails([]);
    setSessionDetailsData([]);
    setSubjectSessionStats({ total: 0, pending: 0, marked: 0, missed: 0 });
    setTotalWeeks(0);
    setLoadingReport(false);
    setReportRows([]);
    setSubjectInfo(null);
  };
  const handleDepartmentChange = (event) => {
    setSelectedDepartmentId(event.target.value);
    selectBatch('');
  };
  const handleBatchChange = (event) => {
    selectBatch(event.target.value);
  };
  const handleCampusChange = (event) => {
    reportRequestRef.current += 1;
    const value = event.target.value;
    setSelectedCampus(value);
    setBatchRoster([]);
    setTermInfo(null);
    setSubjectSessionsAll([]);
    setRemainingSessionDetails([]);
    setSessionDetailsData([]);
    setSubjectSessionStats({ total: 0, pending: 0, marked: 0, missed: 0 });
    setTotalWeeks(0);
    setLoadingReport(false);
  };
  const openFacultyStudentModal = (row) => {
    if (!row) return;
    const subjectStats = facultyBatchMatrix.subjects.map((subject) => {
      const counts = row.subjectCounts?.[subject.key] || { present: 0, absent: 0, percentage: null };
      return {
        subjectName: subject.label,
        present: counts.present,
        absent: counts.absent,
        percentage: counts.percentage
      };
    });
    setFacultyStudentModal({
      isOpen: true,
      studentName: row.studentName || 'Unknown Student',
      studentId: row.studentId || '',
      overallPercentage: row.overallPercentage,
      subjectStats
    });
  };
  const closeFacultyStudentModal = () => {
    setFacultyStudentModal({
      isOpen: false,
      studentName: '',
      studentId: '',
      overallPercentage: null,
      subjectStats: []
    });
  };
  const handleDownloadFacultyStudentPDF = () => {
    if (!facultyStudentModal.isOpen || facultyStudentModal.subjectStats.length === 0) return;

    const reportTitle = `${facultyStudentModal.studentName || 'Student'} All Subjects Report`;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const generatedDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const headerBottomY = drawUniversityPdfHeader(doc, pageWidth, reportTitle);
    const infoRows = [
      ['Student Name:', facultyStudentModal.studentName || 'N/A', 'Student ID:', facultyStudentModal.studentId || 'N/A'],
      ['Faculty:', subjectInfo?.faculty || 'N/A', 'Department:', subjectInfo?.department || 'N/A'],
      ['Batch:', selectedBatchName || subjectInfo?.batch || 'N/A', 'Generated Date:', generatedDate]
    ];

    autoTable(doc, {
      startY: headerBottomY + 4.5,
      body: infoRows,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      styles: {
        fontSize: 9.5,
        cellPadding: { top: 1.6, right: 1.8, bottom: 1.6, left: 1.8 },
        minCellHeight: 6.2,
        lineColor: [229, 231, 235],
        lineWidth: 0.3,
        textColor: [55, 65, 81],
        overflow: 'linebreak',
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 34 },
        1: { cellWidth: 56 },
        2: { cellWidth: 34 },
        3: { cellWidth: 56 }
      },
      didParseCell: (data) => {
        if (data.section === 'body' && (data.column.index === 0 || data.column.index === 2)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [31, 41, 55];
        }
      }
    });

    const bodyRows = facultyStudentModal.subjectStats.map((item) => [
      item.subjectName || 'N/A',
      String(item.present ?? 0),
      String(item.absent ?? 0),
      item.percentage === null || item.percentage === undefined ? '-' : `${item.percentage}%`
    ]);

    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 56) + 8,
      head: [['Subject', 'Present', 'Absent', 'Attendance %']],
      body: bodyRows,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 2.4, textColor: [31, 41, 55], lineColor: [209, 213, 219], lineWidth: 0.28 },
      headStyles: {
        fillColor: [30, 64, 175],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        valign: 'middle',
        minCellHeight: 8
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'center', cellWidth: 28 },
        2: { halign: 'center', cellWidth: 28 },
        3: { halign: 'center', cellWidth: 38 }
      },
      didParseCell: (data) => {
        if (data.section === 'head' && data.column.index === 0) {
          data.cell.styles.halign = 'left';
        }
        if (data.section === 'body' && data.column.index === 3) {
          const rawValue = String(data.cell.raw || '').replace('%', '');
          const percentage = Number(rawValue);
          if (Number.isFinite(percentage)) {
            if (percentage < 50) data.cell.styles.textColor = [185, 28, 28];
            else if (percentage > 75) data.cell.styles.textColor = [21, 128, 61];
            else data.cell.styles.textColor = [146, 64, 14];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    });

    const summaryStartY = (doc.lastAutoTable?.finalY || 120) + 10;
    doc.setFillColor(239, 246, 255);
    doc.setDrawColor(191, 219, 254);
    doc.roundedRect(14, summaryStartY - 6, pageWidth - 28, 16, 2, 2, 'FD');
    doc.setFontSize(10.5);
    doc.setTextColor(30, 64, 175);
    doc.setFont(undefined, 'bold');
    doc.text('Overall Attendance', 20, summaryStartY);
    doc.setFontSize(12.5);
    const overallValue = facultyStudentModal.overallPercentage;
    if (overallValue === null) doc.setTextColor(107, 114, 128);
    else if (overallValue < 50) doc.setTextColor(185, 28, 28);
    else if (overallValue > 75) doc.setTextColor(21, 128, 61);
    else doc.setTextColor(146, 64, 14);
    doc.text(formatAttendancePercentage(overallValue), pageWidth - 20, summaryStartY, { align: 'right' });
    doc.setFont(undefined, 'normal');

    const fileName = `${sanitizeFileName(reportTitle)}.pdf`;
    doc.save(fileName);
  };

  const handleTodayFilter = () => {
    const today = getTodayDateKey();
    setFromDate(today);
    setToDate(today);
    setIsTodayFilterActive(true);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
    setIsTodayFilterActive(false);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
    setIsTodayFilterActive(false);
  };

  const handleClearDateFilters = () => {
    setFromDate('');
    setToDate('');
    setIsTodayFilterActive(false);
  };

  const getPercentageBadgeClass = (percentage) => {
    if (percentage === null || percentage === undefined) {
      return 'bg-gray-100 text-gray-600 border border-gray-200';
    }
    if (percentage < 50) {
      return 'bg-red-100 text-red-800 border border-red-200';
    }
    if (percentage > 75) {
      return 'bg-green-100 text-green-800 border border-green-200';
    }
    return 'bg-amber-100 text-amber-800 border border-amber-200';
  };

  const handleDownloadPDF = () => {
    const exportRows = useFacultyBatchMatrixView ? facultyVisibleRows : visibleRows;
    if (exportRows.length === 0) return;

    const batchLabel = selectedBatchName || subjectInfo?.batch || 'N/A';
    const useDateFilteredAbsentPeriodsPdf =
      Boolean(selectedSubjectId) && hasDateFilter;
    const dateRange = normalizeDateRange(fromDate, toDate);
    const formattedFromDate = formatDateKeyForTitle(dateRange.from);
    const formattedToDate = formatDateKeyForTitle(dateRange.to);
    const dateLabel =
      formattedFromDate && formattedToDate && dateRange.from !== dateRange.to
        ? `${formattedFromDate} - ${formattedToDate}`
        : formattedFromDate || formattedToDate;
    const aboveValue = parsePercentageSelection(abovePercentage);
    const belowValue = parsePercentageSelection(belowPercentage);
    const percentageTitleParts = [];
    if (aboveValue !== null) percentageTitleParts.push(`Above ${aboveValue}%`);
    if (belowValue !== null) percentageTitleParts.push(`Below ${belowValue}%`);
    const percentageTitleSuffix =
      percentageTitleParts.length > 0 ? ` ${percentageTitleParts.join(' and ')}` : '';
    const hasSelectedSubject = Boolean(selectedSubjectId);
    const reportTitleBase = useDateFilteredAbsentPeriodsPdf
      ? `Absent Students of Subject ${selectedSubjectName || 'Subject'} (${dateLabel || 'N/A'})`
      : isFacultyReportView
      ? hasSelectedSubject
        ? `${selectedSubjectName || 'Subject'} Attendance Report`
        : `All Subjects of Batch ${batchLabel} Students`
      : `${selectedSubjectName || 'Subject'} Attendance Report`;
    const reportTitle = `${reportTitleBase}${percentageTitleSuffix}`;

    const useLandscape = useFacultyBatchMatrixView && facultyBatchMatrix.subjects.length > 4;
    const doc = new jsPDF({ orientation: useLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4', compress: true });
    const generatedDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const headerBottomY = drawUniversityPdfHeader(doc, pageWidth, reportTitle);

    const infoRows = useFacultyBatchMatrixView
      ? [
          ['Faculty:', subjectInfo?.faculty || 'N/A', 'Scope:', hasSelectedSubject ? 'Subject Report' : 'Batch Report (All Subjects)'],
          ['Department:', subjectInfo?.department || 'N/A', 'Batch:', selectedBatchName || subjectInfo?.batch || 'N/A'],
          ['Generated Date:', generatedDate, 'Total Students:', String(exportRows.length)]
        ]
      : isFacultyReportView
      ? [
          ['Faculty:', subjectInfo?.faculty || 'N/A', 'Subject:', selectedSubjectName || 'N/A'],
          ['Department:', subjectInfo?.department || 'N/A', 'Batch:', selectedBatchName || subjectInfo?.batch || 'N/A'],
          ['Generated Date:', generatedDate, 'Total Students:', String(exportRows.length)]
        ]
      : [
          ['Faculty:', subjectInfo?.faculty || 'N/A', 'Subject:', selectedSubjectName],
          ['Department:', subjectInfo?.department || 'N/A', 'Lecture Name:', subjectInfo?.tutorName || user?.name || 'N/A'],
          ['Batch:', subjectInfo?.batch || 'N/A', 'Date:', generatedDate],
          ['Total Students:', String(exportRows.length), '', '']
        ];

    autoTable(doc, {
      startY: headerBottomY + 4.5,
      body: infoRows,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      styles: {
        fontSize: 9.5,
        cellPadding: { top: 1.6, right: 1.8, bottom: 1.6, left: 1.8 },
        minCellHeight: 6.2,
        lineColor: [229, 231, 235],
        lineWidth: 0.3,
        textColor: [55, 65, 81],
        overflow: 'linebreak',
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 62 },
        2: { cellWidth: 28 },
        3: { cellWidth: 62 }
      },
      didParseCell: (data) => {
        if (data.section === 'body' && (data.column.index === 0 || data.column.index === 2)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [31, 41, 55];
        }
        if (data.section === 'body' && data.row.index === 3 && (data.column.index === 2 || data.column.index === 3)) {
          data.cell.styles.lineWidth = 0;
          data.cell.styles.fillColor = [255, 255, 255];
          data.cell.text = [''];
        }
      }
    });

    const tableHead = useDateFilteredAbsentPeriodsPdf
      ? [['Student ID', 'Student Name', 'Absent Periods']]
      : useFacultyBatchMatrixView
      ? [
          [
            'Student ID',
            'Student Name',
            ...facultyBatchMatrix.subjects.map((subject) => subject.label),
            'Attendance %'
          ]
        ]
      : [
          hasDateFilter
            ? ['Student ID', 'Student Name', 'Absent']
            : ['Student ID', 'Student Name', 'Absent', 'Present', 'Attendance Percentage']
        ];

    const tableBody = useDateFilteredAbsentPeriodsPdf
      ? visibleRows.map((row) => {
          const absentRecords = buildStudentAttendanceRecords(
            row?.studentKey,
            'absent',
            sessionDetailsData,
            sessionByIdMap,
            normalizeDateRange(fromDate, toDate),
            subjectInfo?.batch || ''
          );
          return [row.studentId, row.studentName, formatAbsentPeriodsSummary(absentRecords)];
        })
      : useFacultyBatchMatrixView
      ? exportRows.map((row) => [
          row.studentId,
          row.studentName,
          ...facultyBatchMatrix.subjects.map((subject) => {
            const value = row.subjectPercentages?.[subject.key];
            return value === null || value === undefined ? '-' : `${value}%`;
          }),
          formatAttendancePercentage(row.overallPercentage)
        ])
      : hasDateFilter
      ? exportRows.map((row) => [row.studentId, row.studentName, row.absent])
      : exportRows.map((row) => [
          row.studentId,
          row.studentName,
          row.absent,
          row.present,
          formatAttendancePercentage(row.percentage)
        ]);

    const attendanceColumnIndex = tableHead[0].length - 1;
    const shouldStylePercentageColumn = !useDateFilteredAbsentPeriodsPdf;

    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 56) + 8,
      head: tableHead,
      body: tableBody,
      theme: 'grid',
      margin: { left: 10, right: 10 },
      styles: { fontSize: isFacultyReportView ? 8.2 : 9, cellPadding: 2.4, textColor: [31, 41, 55] },
      headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold' },
      didParseCell: (data) => {
        if (shouldStylePercentageColumn && data.section === 'body' && data.column.index === attendanceColumnIndex) {
          const rawValue = String(data.cell.raw || '').replace('%', '');
          const percentage = Number(rawValue);
          if (Number.isFinite(percentage)) {
            if (percentage < 50) {
              data.cell.styles.textColor = [185, 28, 28];
            } else if (percentage > 75) {
              data.cell.styles.textColor = [21, 128, 61];
            } else {
              data.cell.styles.textColor = [146, 64, 14];
            }
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages();
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.text(`Page ${pageNumber} of ${pageCount}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
      }
    });

    const fileName = `${sanitizeFileName(reportTitle)}.pdf`;
    doc.save(fileName);
  };

  const handleDownloadExcel = () => {
    if (activeVisibleRows.length === 0) return;

    if (useFacultyBatchMatrixView) {
      const worksheetData = facultyVisibleRows.map((row) => {
        const values = { 'Student ID': row.studentId, 'Student Name': row.studentName };
        facultyBatchMatrix.subjects.forEach((subject) => {
          const percentage = row.subjectPercentages?.[subject.key];
          values[subject.label] = percentage === null || percentage === undefined ? '-' : percentage / 100;
        });
        values['Attendance Percentage'] = row.overallPercentage === null ? '-' : row.overallPercentage / 100;
        return values;
      });
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      worksheet['!cols'] = [{ wch: 16 }, { wch: 32 },
        ...facultyBatchMatrix.subjects.map(() => ({ wch: 22 })), { wch: 24 }];
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let rowIndex = range.s.r + 1; rowIndex <= range.e.r; rowIndex += 1) {
        for (let column = 2; column <= facultyBatchMatrix.subjects.length + 2; column += 1) {
          const cell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: column })];
          if (cell?.t === 'n') cell.z = '0%';
        }
      }
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Batch Attendance');
      XLSX.writeFile(workbook, `batch_attendance_${sanitizeFileName(selectedBatchName)}.xlsx`);
      return;
    }

    const worksheetData = hasDateFilter
      ? visibleRows.map((row) => ({
          'Student ID': row.studentId,
          'Student Name': row.studentName,
          Absent: row.absent,
          ...(!isFacultyBatchReportView ? {
            'Absent Periods': formatAbsentPeriodsSummary(buildStudentAttendanceRecords(
              row.studentKey, 'absent', sessionDetailsData, sessionByIdMap,
              normalizeDateRange(fromDate, toDate), subjectInfo?.batch || ''
            ))
          } : {})
        }))
      : visibleRows.map((row) => ({
          'Student ID': row.studentId,
          'Student Name': row.studentName,
          'Total Attendance': row.totalAttendance,
          Present: row.present,
          Absent: row.absent,
          'Attendance %': row.percentage === null ? '-' : row.percentage / 100
        }));

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    worksheet['!cols'] = hasDateFilter
      ? [{ wch: 15 }, { wch: 30 }, { wch: 10 }, ...(!isFacultyBatchReportView ? [{ wch: 28 }] : [])]
      : [{ wch: 15 }, { wch: 30 }, { wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 14 }];

    if (!hasDateFilter) {
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:F1');
      for (let rowIdx = range.s.r + 1; rowIdx <= range.e.r; rowIdx += 1) {
        const percentageCell = XLSX.utils.encode_cell({ r: rowIdx, c: 5 });
        if (worksheet[percentageCell]) {
          worksheet[percentageCell].z = '0%';
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Report');
    const fileName = `attendance_report_${sanitizeFileName(selectedSubjectName)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const openSessionDetailsModal = (status) => {
    if (!selectedSubjectId && !(isFacultyBatchReportView && selectedBatchId)) return;

    const statusLabel = status ? `${status} session` : 'Session';
    const normalizedStatus = status ? status.toLowerCase() : '';
    const filteredSessions = (normalizedStatus === 'pending'
      ? remainingSessionDetails : subjectSessionsAll).filter((session) => {
      if (!normalizedStatus) return true;
      return (session.state || session.status || '').toLowerCase() === normalizedStatus;
    });

    setSessionDetailsModal({
      isOpen: true,
      title: `${capitalizeLabel(statusLabel)} Details`,
      sessions: filteredSessions
    });
  };

  const closeSessionDetailsModal = () => {
    setSessionDetailsModal({ isOpen: false, title: '', sessions: [] });
  };

  const openStudentAttendanceModal = (row, statusType) => {
    const dateRange = normalizeDateRange(fromDate, toDate);
    const absentRecords = buildStudentAttendanceRecords(
      row?.studentKey,
      'absent',
      sessionDetailsData,
      sessionByIdMap,
      dateRange,
      subjectInfo?.batch || ''
    );
    const presentRecords = buildStudentAttendanceRecords(
      row?.studentKey,
      'present',
      sessionDetailsData,
      sessionByIdMap,
      dateRange,
      subjectInfo?.batch || ''
    );
    const records = buildStudentAttendanceRecords(
      row?.studentKey,
      statusType,
      sessionDetailsData,
      sessionByIdMap,
      dateRange,
      subjectInfo?.batch || ''
    );

    const statusLabel = statusType === 'present' ? 'Present' : 'Absent';
    setStudentAttendanceModal({
      isOpen: true,
      title: `${row?.studentName || 'Student'} - ${statusLabel} Sessions`,
      statusType,
      studentName: row?.studentName || 'Unknown Student',
      studentId: row?.studentId || '',
      faculty: subjectInfo?.faculty || 'N/A',
      department: subjectInfo?.department || 'N/A',
      batch: subjectInfo?.batch || records.find((record) => record.batchName)?.batchName || 'N/A',
      totalAbsent: absentRecords.length,
      totalPresent: presentRecords.length,
      records
    });
  };

  const handleDownloadStudentDetailsPDF = () => {
    if (studentAttendanceModal.records.length === 0) return;

    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    const pageWidth = doc.internal.pageSize.getWidth();
    const generatedDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });

    const headerBottomY = drawUniversityPdfHeader(doc, pageWidth);
    const infoRows = [
      ['System:', 'Attendance System', 'Student Name:', studentAttendanceModal.studentName || 'N/A'],
      ['Student ID:', studentAttendanceModal.studentId || 'N/A', 'Faculty:', studentAttendanceModal.faculty || 'N/A'],
      ['Department:', studentAttendanceModal.department || 'N/A', 'Batch:', studentAttendanceModal.batch || 'N/A']
    ];

    autoTable(doc, {
      startY: headerBottomY + 4.5,
      body: infoRows,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      styles: {
        fontSize: 9.5,
        cellPadding: { top: 1.6, right: 1.8, bottom: 1.6, left: 1.8 },
        minCellHeight: 6.2,
        lineColor: [229, 231, 235],
        lineWidth: 0.3,
        textColor: [55, 65, 81],
        overflow: 'linebreak',
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 62 },
        2: { cellWidth: 28 },
        3: { cellWidth: 62 }
      },
      didParseCell: (data) => {
        if (data.section === 'body' && (data.column.index === 0 || data.column.index === 2)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [31, 41, 55];
        }
      }
    });

    const tableBody = studentAttendanceModal.records.map((record) => [
      record.dateLabel,
      record.dayLabel,
      record.periodLabel,
      record.subjectName,
      capitalizeLabel(record.status)
    ]);

    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 56) + 8,
      head: [['Date', 'Day', 'Period', 'Subject', 'Status']],
      body: tableBody,
      theme: 'grid',
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 2.2, textColor: [31, 41, 55] },
      headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 24 },
        2: { cellWidth: 54 },
        3: { cellWidth: 46 },
        4: { cellWidth: 20 }
      }
    });

    const footerStartY = (doc.lastAutoTable?.finalY || 100) + 10;
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81);
    doc.text(`Total Absent Count: ${studentAttendanceModal.totalAbsent}`, 14, footerStartY);
    doc.text(`Total Present Count: ${studentAttendanceModal.totalPresent}`, 14, footerStartY + 6);
    doc.text(`Generated Date: ${generatedDate}`, 14, footerStartY + 12);

    const fileName = `attendance_details_${sanitizeFileName(studentAttendanceModal.studentName || 'student')}.pdf`;
    doc.save(fileName);
  };

  const closeStudentAttendanceModal = () => {
    setStudentAttendanceModal({
      isOpen: false,
      title: '',
      statusType: 'absent',
      studentName: '',
      studentId: '',
      faculty: '',
      department: '',
      batch: '',
      totalAbsent: 0,
      totalPresent: 0,
      records: []
    });
  };


  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3 mb-2">
          <MdAssessment className="text-3xl text-blue-600" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            {isFacultyBatchReportView ? 'Batch Attendance Report' : isFacultySubjectReportView ? 'Subject Attendance Report' : 'Attendance Reports'}
          </h1>
        </div>
        {!isFacultyBatchReportView && (
          <p className="text-gray-600">Track subject-wise student attendance performance.</p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
        {isFacultyReportView && (
          <div className="flex flex-nowrap items-start gap-4 overflow-x-auto pb-1">
            <div className="flex-1 basis-0 min-w-[190px]">
              <label htmlFor="departmentFilter" className="block text-sm font-semibold text-gray-700 mb-2">
                Select Department
              </label>
              <select
                id="departmentFilter"
                value={selectedDepartmentId}
                onChange={handleDepartmentChange}
                disabled={loadingSubjects}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">{loadingSubjects ? 'Loading departments...' : 'Select a department'}</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>{department.name}</option>
                ))}
              </select>
              {!loadingSubjects && departments.length === 0 && (
                <p className="mt-3 text-sm text-amber-700">No active departments found for your faculty.</p>
              )}
            </div>
            <div className="flex-1 basis-0 min-w-[190px]">
              <label htmlFor="batchFilter" className="block text-sm font-semibold text-gray-700 mb-2">
                Select Batch
              </label>
              <select
                id="batchFilter"
                value={selectedBatchId}
                onChange={handleBatchChange}
                disabled={loadingSubjects || !selectedDepartmentId}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">
                  {loadingSubjects
                    ? 'Loading batches...'
                    : selectedDepartmentId ? 'Select a batch' : 'Select a department first'}
                </option>
                {departmentBatches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.name}
                  </option>
                ))}
              </select>
              {!loadingSubjects && selectedDepartmentId && departmentBatches.length === 0 && (
                <p className="mt-3 text-sm text-amber-700">
                  No active batches found for this department.
                </p>
              )}
            </div>
            {isFacultySubjectReportView && <div className="flex-1 basis-0 min-w-[190px]">
              <label htmlFor="subjectFilter" className="block text-sm font-semibold text-gray-700 mb-2">
                Select Subject
              </label>
              <select
                id="subjectFilter"
                value={selectedSubjectId}
                onChange={handleSubjectChange}
                disabled={loadingSubjects || !selectedBatchId}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">
                  {loadingSubjects ? 'Loading subjects...' : selectedBatchId ? 'Select a subject' : 'Select a batch first'}
                </option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>{subject.name}</option>
                ))}
              </select>
              {!loadingSubjects && selectedBatchId && subjects.length === 0 && (
                <p className="mt-3 text-sm text-amber-700">No active subjects found for this batch.</p>
              )}
            </div>}
            <div className="flex-1 basis-0 min-w-[190px]">
              <label htmlFor="campusFilter" className="block text-sm font-semibold text-gray-700 mb-2">
                Select Campus (optional)
              </label>
              <select
                id="campusFilter"
                value={selectedCampus}
                onChange={handleCampusChange}
                disabled={loadingSubjects || !selectedBatchId}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">All campuses</option>
                {availableCampusOptions.map((campus) => (
                  <option key={campus.value} value={campus.value}>{campus.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {!isFacultyReportView && <>
          <label htmlFor="subjectFilter" className="block text-sm font-semibold text-gray-700 mb-2">
            Select Subject
          </label>
          <select
            id="subjectFilter"
            value={selectedSubjectId}
            onChange={handleSubjectChange}
            disabled={loadingSubjects || (isFacultyReportView && !selectedBatchId)}
            className="w-full md:w-96 xl:w-96 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="">
              {loadingSubjects
                ? 'Loading subjects...'
                : isFacultyReportView && !selectedBatchId
                ? 'Select a batch first'
                : 'Select a subject'}
            </option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          {!loadingSubjects && subjects.length === 0 && (!isFacultyReportView || selectedBatchId) && (
            <p className="mt-3 text-sm text-amber-700">
              No assigned subjects found for your selection.
            </p>
          )}
        </>}
      </div>

      {termInfo && (isFacultyReportView ? selectedBatchId : selectedSubjectId) && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          <span className="font-semibold">Current academic term:</span>{' '}
          {[termInfo.academic_year, termInfo.academic_term]
            .filter(Boolean).join(' · ')}
          {termInfo.start_date && termInfo.end_date && (
            <span className="ml-2">({termInfo.start_date} to {termInfo.end_date})</span>
          )}
          {termInfo.batch_semester && (
            <span className="ml-2">· Batch {termInfo.batch_semester}</span>
          )}
          {subjectSessionStats.marked === 0 && (
            <span className="block mt-1">No attendance has been marked for this selection yet; missed sessions are not student absences.</span>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {!((isFacultyBatchReportView && selectedBatchId) ||
          (isFacultySubjectReportView && selectedBatchId && selectedSubjectId) ||
          (!isFacultyReportView && selectedSubjectId)) && !loadingSubjects && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-10 text-center">
          <p className="text-gray-600 text-lg">
            {isFacultyBatchReportView
              ? 'Please select a batch to view attendance report'
              : isFacultySubjectReportView && !selectedBatchId
              ? 'Please select a batch to view attendance report'
              : 'Please select a subject to view attendance report'}
          </p>
        </div>
      )}

      {((isFacultyBatchReportView && selectedBatchId) ||
        (isFacultySubjectReportView && selectedBatchId && selectedSubjectId) ||
        (!isFacultyReportView && selectedSubjectId)) && (
        <>
          {loadingReport ? (
            <div className="flex items-center justify-center h-56 bg-white border border-gray-200 rounded-xl">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto" />
                <p className="mt-3 text-gray-600">Loading attendance report...</p>
              </div>
            </div>
          ) : (
            <>
              {!isFacultyBatchReportView && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total sessions this semester</p>
                      <p className="text-2xl font-bold text-gray-800">{subjectSessionStats.total}</p>
                    </div>
                    <MdCalendarToday className="text-3xl text-blue-500" />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openSessionDetailsModal('marked')}
                  className="text-left bg-white border border-green-200 rounded-xl p-5 shadow-sm transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-green-300"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Marked sessions</p>
                      <p className="text-2xl font-bold text-green-600">{subjectSessionStats.marked}</p>
                      <p className="text-xs text-green-700 mt-1">Click to view details</p>
                    </div>
                    <MdCheckCircle className="text-3xl text-green-500" />
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => openSessionDetailsModal('missed')}
                  className="text-left bg-white border border-red-200 rounded-xl p-5 shadow-sm transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-red-300"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Missed sessions</p>
                      <p className="text-2xl font-bold text-red-600">{subjectSessionStats.missed}</p>
                      <p className="text-xs text-red-700 mt-1">Click to view details</p>
                    </div>
                    <MdCancel className="text-3xl text-red-500" />
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => openSessionDetailsModal('pending')}
                  className="text-left bg-white border border-yellow-200 rounded-xl p-5 shadow-sm transition hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Remaining sessions</p>
                      <p className="text-2xl font-bold text-yellow-600">{subjectSessionStats.pending}</p>
                      <p className="text-xs text-yellow-700 mt-1">Click to view details</p>
                    </div>
                    <MdAccessTime className="text-3xl text-yellow-500" />
                  </div>
                </button>
              </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Students</p>
                      <p className="text-2xl font-bold text-gray-800">{totalStudents}</p>
                    </div>
                    <MdPeople className="text-3xl text-blue-500" />
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Average Attendance %</p>
                      <p className="text-2xl font-bold text-blue-600">{formatAttendancePercentage(averageAttendance)}</p>
                    </div>
                    <MdPercent className="text-3xl text-blue-500" />
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Weeks</p>
                      <p className="text-2xl font-bold text-gray-800">{totalWeeks}</p>
                    </div>
                    <MdCalendarToday className="text-3xl text-blue-500" />
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-sm text-gray-500">Subject Information</p>
                    </div>
                    <MdSchool className="text-3xl text-blue-500" />
                  </div>
                  <div className="space-y-3.5">
                    <div>
                      <p className="text-xs text-gray-500">Faculty</p>
                      <p className="mt-1 text-sm font-semibold text-gray-900 leading-snug break-words">
                        {subjectInfo?.faculty || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Department</p>
                      <p className="mt-1 text-sm font-semibold text-gray-900 leading-snug break-words">
                        {subjectInfo?.department || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Batch</p>
                      <p className="mt-1">
                        <span className="inline-flex px-2.5 py-1 rounded-md bg-gray-100 text-gray-800 text-xs font-semibold">
                        {subjectInfo?.batch || selectedBatchName || 'N/A'}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 shadow-sm">
                <div className="mb-4">
                  <div className="flex flex-col md:flex-row xl:flex-row gap-3 md:items-end xl:items-end">
                    <div className="relative w-full md:flex-1 xl:flex-1">
                      <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xl" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by student name or ID"
                        className="w-full h-[42px] pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="w-full md:w-44 xl:w-44">
                      <PercentageComboDropdown
                        label="Above %"
                        placeholder="Above %"
                        value={abovePercentage}
                        onChange={setAbovePercentage}
                        options={percentageOptions}
                        wrapperClassName="w-full"
                      />
                    </div>
                    <div className="w-full md:w-44 xl:w-44">
                      <PercentageComboDropdown
                        label="Below %"
                        placeholder="Below %"
                        value={belowPercentage}
                        onChange={setBelowPercentage}
                        options={percentageOptions}
                        wrapperClassName="w-full"
                      />
                    </div>
                  </div>
                  <div className="mt-3 flex flex-col md:flex-row xl:flex-row md:flex-nowrap xl:flex-nowrap gap-3 md:items-end xl:items-end">
                    <div className="w-full md:flex-1 xl:flex-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={handleFromDateChange}
                        className="w-full h-[42px] px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>
                    <div className="w-full md:flex-1 xl:flex-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={handleToDateChange}
                        className="w-full h-[42px] px-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                    </div>
                    {!isFacultyBatchReportView && <div className="w-full md:w-44 xl:w-44">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Quick Filter</label>
                      <button
                        type="button"
                        onClick={handleTodayFilter}
                        className="w-full h-[42px] px-3 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                      >
                        Today
                      </button>
                    </div>}
                    {!isFacultyBatchReportView && <div className="w-full md:w-44 xl:w-44">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Reset</label>
                      <button
                        type="button"
                        onClick={handleClearDateFilters}
                        disabled={!fromDate && !toDate && !isTodayFilterActive}
                        className="w-full h-[42px] px-3 border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Clear Filters
                      </button>
                    </div>}
                  </div>
                  <label className="inline-flex items-center space-x-2 text-sm text-gray-700 mt-3">
                    <MdFilterList className="text-lg text-gray-500" />
                    <input
                      type="checkbox"
                      checked={showLowAttendanceOnly}
                      onChange={(e) => setShowLowAttendanceOnly(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>Show low attendance (&lt; 75%)</span>
                  </label>
                </div>
                <div className="flex flex-wrap justify-end gap-2 mb-4">
                  <button
                    type="button"
                    onClick={handleDownloadPDF}
                    disabled={activeVisibleRows.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <MdDownload />
                    <span>Download PDF</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadExcel}
                    disabled={activeVisibleRows.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <MdDownload />
                    <span>Download Excel</span>
                  </button>
                </div>

                {attendanceRows.length === 0 ? (
                  <div className="py-10 text-center text-gray-600">
                    {isFacultyBatchReportView ? 'No students found in this batch' : 'No students found for this subject'}
                  </div>
                ) : activeVisibleRows.length === 0 ? (
                  <div className="py-10 text-center text-gray-600">
                    {!isFacultyBatchReportView && hasDateFilter
                      ? 'No students have recorded absences matching the selected dates and filters'
                      : 'No students match the current filters'}
                  </div>
                ) : (
                  <>
                    {useFacultyBatchMatrixView ? (
                      <>
                        <div className="table-view hidden md:block xl:block overflow-x-auto rounded-xl border border-gray-200 shadow-sm bg-white">
                          <table className="min-w-[1100px] w-full border-separate border-spacing-0">
                            <thead className="bg-slate-50">
                              <tr className="text-left">
                                <th className="sticky top-0 z-10 px-5 py-3.5 text-xs font-bold uppercase tracking-wide text-slate-700 bg-slate-50 border-b border-slate-200">Student ID</th>
                                <th className="sticky top-0 z-10 px-5 py-3.5 text-xs font-bold uppercase tracking-wide text-slate-700 bg-slate-50 border-b border-slate-200">Student Name</th>
                                {facultyBatchMatrix.subjects.map((subject) => (
                                  <th key={subject.key} className="sticky top-0 z-10 min-w-[140px] px-5 py-3.5 text-xs font-bold uppercase tracking-wide text-slate-700 bg-slate-50 border-b border-slate-200">
                                    {subject.label}
                                  </th>
                                ))}
                                <th className="sticky top-0 z-10 px-5 py-3.5 text-xs font-bold uppercase tracking-wide text-slate-700 bg-slate-50 border-b border-slate-200">Attendance Percentage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {facultyVisibleRows.map((row, rowIndex) => (
                                <tr
                                  key={row.studentId}
                                  onClick={() => openFacultyStudentModal(row)}
                                  className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/60 transition-colors cursor-pointer`}
                                  title="Click to view student details"
                                >
                                  <td className="px-5 py-3.5 text-sm font-medium text-slate-700 border-b border-slate-100 whitespace-nowrap">{row.studentId}</td>
                                  <td className="px-5 py-3.5 text-sm font-semibold text-slate-800 border-b border-slate-100 whitespace-nowrap">{row.studentName}</td>
                                  {facultyBatchMatrix.subjects.map((subject) => {
                                    const value = row.subjectPercentages?.[subject.key];
                                    return (
                                      <td key={`${row.studentId}-${subject.key}`} className="px-5 py-3.5 text-sm text-slate-700 border-b border-slate-100 text-center whitespace-nowrap">
                                        {value === null || value === undefined ? '-' : `${value}%`}
                                      </td>
                                    );
                                  })}
                                  <td className="px-5 py-3.5 text-sm border-b border-slate-100 whitespace-nowrap">
                                    <span className={`inline-flex items-center justify-center min-w-[68px] px-3 py-1.5 rounded-full text-xs font-bold ${getPercentageBadgeClass(row.overallPercentage)}`}>
                                      {formatAttendancePercentage(row.overallPercentage)}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="card-view md:hidden xl:hidden space-y-3">
                          {facultyVisibleRows.map((row) => (
                            <div
                              key={row.studentId}
                              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm cursor-pointer hover:shadow-md transition"
                              onClick={() => openFacultyStudentModal(row)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  openFacultyStudentModal(row);
                                }
                              }}
                              title="Click to view student details"
                            >
                              <p className="text-xs text-gray-500">{row.studentId}</p>
                              <p className="mt-1 text-base font-bold text-gray-900 leading-snug">{row.studentName}</p>
                              <div className="mt-3 grid grid-cols-2 gap-2.5 text-sm">
                                {facultyBatchMatrix.subjects.map((subject) => {
                                  const value = row.subjectPercentages?.[subject.key];
                                  return (
                                    <p key={`${row.studentId}-mobile-${subject.key}`} className="text-gray-600 leading-5">
                                      {`${subject.label}: `}<span className="font-semibold text-gray-900">{value === null || value === undefined ? '-' : `${value}%`}</span>
                                    </p>
                                  );
                                })}
                              </div>
                              <div className="mt-3">
                                <span className={`inline-flex items-center justify-center min-w-[78px] px-3 py-1.5 rounded-full text-xs font-bold ${getPercentageBadgeClass(row.overallPercentage)}`}>
                                  Attendance: {formatAttendancePercentage(row.overallPercentage)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="table-view hidden md:block xl:block overflow-x-auto rounded-lg border border-gray-200">
                          <table className="min-w-[880px] w-full">
                            <thead className="bg-gray-50">
                              <tr className="text-left">
                                <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600">Student ID</th>
                                <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600">Student Name</th>
                                <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600">Absent</th>
                                {!hasDateFilter && (
                                  <>
                                    <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600">Present</th>
                                    <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600">Attendance Percentage</th>
                                    <th className="px-4 py-3 text-xs font-semibold uppercase text-gray-600">Total Attendance</th>
                                  </>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {visibleRows.map((row) => (
                                <tr key={row.studentId} className="border-t border-gray-100 hover:bg-blue-50 transition-colors">
                                  <td className="px-4 py-3 text-sm text-gray-700">{row.studentId}</td>
                                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.studentName}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <button
                                      type="button"
                                      onClick={() => openStudentAttendanceModal(row, 'absent')}
                                      title="Click to view details"
                                      className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200 shadow-sm cursor-pointer transition-all duration-200 hover:bg-red-200 hover:shadow-md hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300"
                                    >
                                      <span className="text-[10px] uppercase tracking-wide">View</span>
                                      {row.absent}
                                    </button>
                                  </td>
                                  {!hasDateFilter && (
                                    <>
                                      <td className="px-4 py-3 text-sm">
                                        <button
                                          type="button"
                                          onClick={() => openStudentAttendanceModal(row, 'present')}
                                          title="Click to view details"
                                          className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200 shadow-sm cursor-pointer transition-all duration-200 hover:bg-green-200 hover:shadow-md hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-300"
                                        >
                                          <span className="text-[10px] uppercase tracking-wide">View</span>
                                          {row.present}
                                        </button>
                                      </td>
                                      <td className="px-4 py-3 text-sm">
                                        <span
                                          className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getPercentageBadgeClass(
                                            row.percentage
                                          )}`}
                                          title={
                                            row.percentage === null
                                              ? 'No attendance marked yet'
                                              : row.percentage < 50
                                              ? 'Low attendance (at risk)'
                                              : row.percentage > 75
                                              ? 'High attendance (good performance)'
                                              : 'Moderate attendance'
                                          }
                                        >
                                          {formatAttendancePercentage(row.percentage)}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700">{row.totalAttendance}</td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="card-view md:hidden xl:hidden space-y-3">
                          {visibleRows.map((row) => (
                            <div
                              key={row.studentId}
                              className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                            >
                              <p className="text-xs text-gray-500">{row.studentId}</p>
                              <p className="mt-1 text-base font-bold text-gray-900 leading-snug">{row.studentName}</p>
                              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                                <button
                                  type="button"
                                  onClick={() => openStudentAttendanceModal(row, 'absent')}
                                  title="Click to view details"
                                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 font-semibold text-red-700 cursor-pointer transition-all duration-200 hover:bg-red-100 hover:shadow-md hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-300"
                                >
                                  Absent: {row.absent}
                                </button>
                                {!hasDateFilter && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openStudentAttendanceModal(row, 'present')}
                                      title="Click to view details"
                                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 font-semibold text-green-700 cursor-pointer transition-all duration-200 hover:bg-green-100 hover:shadow-md hover:scale-105 focus:outline-none focus:ring-2 focus:ring-green-300"
                                    >
                                      Present: {row.present}
                                    </button>
                                    <span className="font-medium text-gray-600">Total: {row.totalAttendance}</span>
                                    <span
                                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${getPercentageBadgeClass(
                                        row.percentage
                                      )}`}
                                      title={
                                        row.percentage === null
                                          ? 'No attendance marked yet'
                                          : row.percentage < 50
                                          ? 'Low attendance (at risk)'
                                          : row.percentage > 75
                                          ? 'High attendance (good performance)'
                                          : 'Moderate attendance'
                                      }
                                    >
                                      {formatAttendancePercentage(row.percentage)}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}

      {sessionDetailsModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4">
          <div className="w-full max-w-3xl bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {sessionDetailsModal.title}
                </h2>
                <p className="text-sm text-gray-500">
                  {sessionDetailsModal.sessions.length} sessions
                </p>
              </div>
              <button
                type="button"
                onClick={closeSessionDetailsModal}
                className="rounded-full p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Close"
              >
                X
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
              {sessionDetailsModal.sessions.length === 0 ? (
                <div className="text-center text-gray-500 py-10">
                  No sessions available for this status.
                </div>
              ) : (
                sessionDetailsModal.sessions.map((session) => {
                  const dateLabel = formatSessionDate(session);
                  const timeLabel = formatSessionPeriodTime(session);
                  const subjectName = session.subject || session.subject_name || 'Untitled Subject';
                  const batchName = subjectInfo?.batch || getSessionBatchName(session);

                  return (
                    <div key={session.id || `${dateLabel}-${timeLabel}-${subjectName}`} className="border border-gray-200 rounded-xl p-4">
                      <p className="text-sm font-semibold text-gray-800">{dateLabel}</p>
                      <div className="mt-2 space-y-1 text-sm text-gray-600">
                        <p>{timeLabel}</p>
                        <p>
                          <span className="font-medium text-gray-700">Subject:</span> {subjectName}
                        </p>
                        <p>
                          <span className="font-medium text-gray-700">Batch:</span> {batchName}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {studentAttendanceModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-gray-200 transition-all">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                  {studentAttendanceModal.title}
                </h2>
                <p className="text-sm text-gray-500">
                  {studentAttendanceModal.records.length} records
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDownloadStudentDetailsPDF}
                  disabled={studentAttendanceModal.records.length === 0}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MdDownload />
                  <span>Download PDF</span>
                </button>
                <button
                  type="button"
                  onClick={closeStudentAttendanceModal}
                  className="rounded-full p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  aria-label="Close"
                >
                  X
                </button>
              </div>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-6 py-4 space-y-4">
              {studentAttendanceModal.records.length === 0 ? (
                <div className="text-center text-gray-500 py-10">
                  {studentAttendanceModal.statusType === 'present'
                    ? 'No present records found'
                    : 'No absence records found'}
                </div>
              ) : (
                studentAttendanceModal.records.map((record, index) => (
                  <div
                    key={`${record.sessionId || 'session'}-${record.status}-${index}`}
                    className="border border-gray-200 rounded-xl p-4"
                  >
                    <p className="text-sm font-semibold text-gray-800">
                      {record.dateLabel} ({record.dayLabel})
                    </p>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p>{record.periodLabel}</p>
                      <p>
                        <span className="font-medium text-gray-700">Subject:</span> {record.subjectName}
                      </p>
                      <p>
                        <span className="font-medium text-gray-700">Batch:</span> {record.batchName || studentAttendanceModal.batch || 'N/A'}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {facultyStudentModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4">
          <div className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{facultyStudentModal.studentName}</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Student ID: {facultyStudentModal.studentId || 'N/A'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-gray-500">Overall Attendance</p>
                <span className={`inline-flex mt-1 px-3 py-1.5 rounded-full text-sm font-bold ${getPercentageBadgeClass(facultyStudentModal.overallPercentage)}`}>
                  {formatAttendancePercentage(facultyStudentModal.overallPercentage)}
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600 border-b border-slate-200">Subject</th>
                      <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-600 border-b border-slate-200">Present</th>
                      <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-600 border-b border-slate-200">Absent</th>
                      <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wide text-slate-600 border-b border-slate-200">Attendance %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facultyStudentModal.subjectStats.map((item, index) => (
                      <tr key={`${item.subjectName}-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                        <td className="px-4 py-3.5 text-sm font-semibold text-gray-800 border-b border-gray-100">{item.subjectName}</td>
                        <td className="px-4 py-3.5 text-sm text-center text-green-700 font-semibold border-b border-gray-100">{item.present}</td>
                        <td className="px-4 py-3.5 text-sm text-center text-red-700 font-semibold border-b border-gray-100">{item.absent}</td>
                        <td className="px-4 py-3.5 text-sm text-center border-b border-gray-100">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getPercentageBadgeClass(Number(item.percentage || 0))}`}>
                            {item.percentage === null ? '-' : `${item.percentage}%`}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDownloadFacultyStudentPDF}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 transition"
              >
                <MdDownload />
                <span>Download PDF</span>
              </button>
              <button
                type="button"
                onClick={closeFacultyStudentModal}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function totalSessionsSafe(value) {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function formatAbsentPeriodsSummary(absentRecords) {
  if (!Array.isArray(absentRecords) || absentRecords.length === 0) {
    return '-';
  }

  const uniquePeriods = Array.from(
    new Set(
      absentRecords
        .map((record) => extractCompactPeriodLabel(record?.periodLabel || '').toLowerCase())
        .filter(Boolean)
    )
  );

  return uniquePeriods.length > 0
    ? uniquePeriods
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((period) => `(${period})`)
      .join(' ')
    : 'Absent';
}

function extractCompactPeriodLabel(periodLabel) {
  const raw = String(periodLabel || '').trim();
  if (!raw) return '';
  const periodMatch = raw.match(/Period\s*\d+/i);
  if (periodMatch) {
    return periodMatch[0].replace(/\s+/g, ' ');
  }
  return raw;
}

function buildSubjectOptions(sessions, batchId = '', batchName = '') {
  const subjectMap = new Map();
  (sessions || []).forEach((session) => {
    if (!session) return;
    if (batchId && !sessionMatchesBatch(session, batchId, batchName)) return;
    const subjectId = String(getSessionSubjectId(session) || '');
    const subjectName = session.subject || session.subject_name || 'Untitled Subject';
    if (!subjectId) return;
    if (!subjectMap.has(subjectId)) {
      subjectMap.set(subjectId, { id: subjectId, name: subjectName });
    }
  });
  return Array.from(subjectMap.values()).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
}

function mergeSubjectOptions(...groups) {
  const subjects = new Map();
  groups.flat().forEach((subject) => {
    if (subject?.id && subject?.name) subjects.set(String(subject.id), subject);
  });
  return Array.from(subjects.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function buildTimetableSubjectOptions(timetables, batchId, campus = '') {
  const subjectMap = new Map();
  (timetables || []).forEach((timetable) => {
    if (!extractTimetableBatches(timetable).some((batch) => String(batch.id) === String(batchId))) return;
    if (campus && timetable.campus !== campus) return;
    (timetable.subjects || []).forEach((subject) => {
      const id = String(subject.subject_id || '').trim();
      const name = String(subject.subject_name || '').trim();
      if (id && name) subjectMap.set(id, { id, name });
    });
  });
  return Array.from(subjectMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function buildTimetableCampusOptions(timetables, batchId) {
  if (!batchId) return [];
  const options = new Map();
  (timetables || []).forEach((timetable) => {
    if (!extractTimetableBatches(timetable).some((batch) => String(batch.id) === String(batchId))) return;
    const value = String(timetable.campus || '').trim();
    if (value) options.set(value, { value, name: timetable.campus_name || value });
  });
  return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function extractSessionBatches(session) {
  const candidates = [];
  const rawBatchLists = [
    session?.batch_ids,
    session?.batch_id,
    session?.batches,
    session?.batch,
    session?.batch_name,
    session?.class_name
  ];

  rawBatchLists.forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((item) => candidates.push(item));
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      candidates.push(value);
    }
  });

  const normalized = [];
  candidates.forEach((item) => {
    if (typeof item === 'object') {
      const id = String(item.id ?? item.batch_id ?? item.value ?? item.name ?? '').trim();
      const name = String(item.name ?? item.batch_name ?? item.label ?? id).trim();
      if (id) normalized.push({ id, name: name || id });
      return;
    }

    const raw = String(item).trim();
    if (!raw) return;
    const values = raw.includes(',') ? raw.split(',').map((part) => part.trim()) : [raw];
    values.filter(Boolean).forEach((value) => normalized.push({ id: value, name: value }));
  });

  const uniqueMap = new Map();
  normalized.forEach((item) => {
    if (!item.id) return;
    if (!uniqueMap.has(item.id)) {
      uniqueMap.set(item.id, item);
    }
  });
  return Array.from(uniqueMap.values());
}

function sessionMatchesBatch(session, selectedBatchId, selectedBatchName = '') {
  if (!selectedBatchId) return true;
  const normalizedSelected = normalizeKey(selectedBatchId);
  const normalizedSelectedName = normalizeKey(selectedBatchName);
  return extractSessionBatches(session).some((batch) => {
    const batchId = normalizeKey(batch.id);
    const batchName = normalizeKey(batch.name);
    return (
      batchId === normalizedSelected ||
      batchName === normalizedSelected ||
      (normalizedSelectedName && (batchId === normalizedSelectedName || batchName === normalizedSelectedName))
    );
  });
}

function extractTimetableBatches(timetable) {
  const candidates = [];
  const rawBatchLists = [
    timetable?.batch_ids,
    timetable?.batch_id,
    timetable?.batches,
    timetable?.batch,
    timetable?.batch_name,
    timetable?.class_name
  ];
  rawBatchLists.forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((item) => candidates.push(item));
      return;
    }
    if (value !== undefined && value !== null && value !== '') {
      candidates.push(value);
    }
  });

  return candidates
    .flatMap((item) => {
      if (typeof item === 'object') {
        const id = String(item.id ?? item.batch_id ?? item.value ?? item.name ?? '').trim();
        const name = String(item.name ?? item.batch_name ?? item.label ?? id).trim();
        return id ? [{ id, name: name || id }] : [];
      }
      const raw = String(item).trim();
      if (!raw) return [];
      const values = raw.includes(',') ? raw.split(',').map((part) => part.trim()) : [raw];
      return values.filter(Boolean).map((value) => ({ id: value, name: value }));
    })
    .filter((item) => item.id);
}

function normalizeKey(value) {
  return String(value || '').trim().toLowerCase();
}

function getSubjectKey(session) {
  const subjectId = getSessionSubjectId(session);
  const subjectName = session?.subject || session?.subject_name || '';
  return String(subjectId || subjectName || '').trim();
}

function getSessionSubjectId(session) {
  if (!session) return '';
  const directId =
    session.subject_id ??
    session.subjectId ??
    session.subjectID ??
    session.subject?.id ??
    session.subject?.subject_id ??
    '';
  return String(directId || '').trim();
}

function getSubjectLabel(session) {
  return String(session?.subject || session?.subject_name || 'Subject').trim();
}

async function fetchSessionAttendanceDetail(sessionId, token, isFacultyReportView) {
  if (!sessionId) return null;

  if (isFacultyReportView) {
    const facultyResult = await attendanceAPI.getFacultySession(sessionId, token).catch(() => null);
    if (facultyResult) {
      const students = Array.isArray(facultyResult.students)
        ? facultyResult.students
        : Array.isArray(facultyResult.session?.students)
        ? facultyResult.session.students
        : [];
      const baseSession = facultyResult.session || {};
      return {
        ...facultyResult,
        session: baseSession,
        session_id: facultyResult.session_id || baseSession.id || sessionId,
        students
      };
    }
  }

  return attendanceAPI.viewAttendance(sessionId, token);
}

function buildAttendanceRows(sessionDetailsList, subjectSessionsAll, fromDate, toDate, roster = null) {
  const dateRange = normalizeDateRange(fromDate, toDate);
  const sessionDateMap = buildSessionDateMap(subjectSessionsAll);
  const studentMap = new Map();
  const allowedStudentIds = roster === null ? null : new Set(
    roster.map((student) => String(student.student_id || student.id || ''))
  );

  (roster || []).forEach((student) => {
    const studentKey = String(student.student_id || student.id || '');
    if (!studentKey) return;
    studentMap.set(studentKey, {
      studentId: getDisplayStudentId(student),
      studentName: cleanStudentName(student.student_name || student.name || 'Unknown Student'),
      present: 0,
      absent: 0
    });
  });

  // Always register all students first so table keeps full roster by default.
  sessionDetailsList.forEach((detail) => {
    if (!detail) return;
    const students = Array.isArray(detail?.students) ? detail.students : [];
    students.forEach((student) => {
      const studentKey = String(student.student_id || student.id || '');
      if (!studentKey || (allowedStudentIds && !allowedStudentIds.has(studentKey))) return;

      if (!studentMap.has(studentKey)) {
        studentMap.set(studentKey, {
          studentId: getDisplayStudentId(student),
          studentName: cleanStudentName(student.student_name || student.name || 'Unknown Student'),
          present: 0,
          absent: 0
        });
      } else {
        const existing = studentMap.get(studentKey);
        if (
          existing.studentName === 'Unknown Student' &&
          (student.student_name || student.name)
        ) {
          existing.studentName = cleanStudentName(student.student_name || student.name);
        }

        const bestStudentId = getDisplayStudentId(student);
        if (bestStudentId && !looksLikePlainNumericId(bestStudentId)) {
          existing.studentId = bestStudentId;
        }
      }
    });
  });

  sessionDetailsList.forEach((detail) => {
    if (!detail) return;
    const detailSessionId = getSessionIdFromDetail(detail);
    const mappedDateKey = detailSessionId ? sessionDateMap.get(String(detailSessionId)) : '';
    const sessionDateKey = mappedDateKey || getSessionDateKey(detail);
    if (dateRange.from || dateRange.to) {
      if (!sessionDateKey) return;
      if (!isDateInRange(sessionDateKey, dateRange)) return;
    }

    const students = Array.isArray(detail?.students) ? detail.students : [];
    students.forEach((student) => {
      const studentKey = String(student.student_id || student.id || '');
      if (!studentKey || (allowedStudentIds && !allowedStudentIds.has(studentKey))) return;

      const existing = studentMap.get(studentKey) || {
        studentId: getDisplayStudentId(student),
        studentName: cleanStudentName(student.student_name || student.name || 'Unknown Student'),
        present: 0,
        absent: 0
      };

      if (student.is_present) {
        existing.present += 1;
      } else {
        existing.absent += 1;
      }

      if (
        existing.studentName === 'Unknown Student' &&
        (student.student_name || student.name)
      ) {
        existing.studentName = cleanStudentName(student.student_name || student.name);
      }

      const bestStudentId = getDisplayStudentId(student);
      if (bestStudentId && !looksLikePlainNumericId(bestStudentId)) {
        existing.studentId = bestStudentId;
      }

      studentMap.set(studentKey, existing);
    });
  });

  const rows = Array.from(studentMap.entries())
    .map(([studentKey, student]) => {
      const totalAttendance = student.present + student.absent;
      const percentage =
        totalAttendance > 0 ? Math.round((student.present / totalAttendance) * 100) : null;

      return {
        ...student,
        studentKey,
        totalAttendance,
        percentage
      };
    });

  rows.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''));
  return rows;
}

function buildStudentAttendanceRecords(
  studentKey,
  statusType,
  sessionDetailsList,
  sessionByIdMap,
  dateRange,
  fallbackBatch = ''
) {
  if (!studentKey || !Array.isArray(sessionDetailsList)) return [];
  const wantsPresent = statusType === 'present';
  const records = [];

  sessionDetailsList.forEach((detail) => {
    if (!detail) return;
    const detailSessionId = getSessionIdFromDetail(detail);
    const session = detailSessionId ? sessionByIdMap.get(String(detailSessionId)) : null;
    const sessionDateKey = normalizeDateKey(
      session?.date ||
        session?.session_date ||
        session?.start_date ||
        session?.start ||
        session?.schedule_date ||
        getSessionDateKey(detail)
    );

    if ((dateRange?.from || dateRange?.to) && (!sessionDateKey || !isDateInRange(sessionDateKey, dateRange))) {
      return;
    }

    const students = Array.isArray(detail.students) ? detail.students : [];
    const student = students.find(
      (item) => String(item?.student_id || item?.id || '') === String(studentKey)
    );
    if (!student) return;

    const isPresent = Boolean(student.is_present);
    if (wantsPresent !== isPresent) return;

    records.push({
      sessionId: detailSessionId || session?.id || '',
      dateKey: sessionDateKey || '',
      status: wantsPresent ? 'present' : 'absent',
      dateLabel: formatSessionDate(session || detail),
      dayLabel: formatSessionDay(session || detail),
      periodLabel: formatSessionPeriodTime(session || detail),
      subjectName: session?.subject || session?.subject_name || detail?.session?.subject || 'N/A',
      batchName: getSessionBatchName(session || detail?.session || {}) || fallbackBatch || 'N/A'
    });
  });

  records.sort((a, b) => {
    const dateA = a.dateKey || '';
    const dateB = b.dateKey || '';
    return dateA.localeCompare(dateB);
  });
  return records;
}

function buildSessionDateMap(subjectSessionsAll) {
  const map = new Map();
  if (!Array.isArray(subjectSessionsAll)) return map;
  subjectSessionsAll.forEach((session) => {
    if (!session) return;
    const sessionId = session.id || session.session_id || session.sessionId;
    if (!sessionId) return;
    const dateKey = normalizeDateKey(
      session.date ||
        session.session_date ||
        session.start_date ||
        session.start ||
        session.schedule_date
    );
    if (dateKey) {
      map.set(String(sessionId), dateKey);
    }
  });
  return map;
}

function filterSessionsByDateRange(sessions, dateRange) {
  if (!Array.isArray(sessions) || sessions.length === 0) return [];
  if (!dateRange?.from && !dateRange?.to) return sessions;

  return sessions.filter((session) => {
    const sessionDateKey = getSessionDateKeyFromSession(session);
    if (!sessionDateKey) return false;
    return isDateInRange(sessionDateKey, dateRange);
  });
}

function getSessionDateKeyFromSession(session) {
  if (!session) return '';
  return normalizeDateKey(
    session.date ||
      session.session_date ||
      session.start_date ||
      session.start ||
      session.schedule_date
  );
}

function getSessionIdFromDetail(detail) {
  return (
    detail?.session_id ||
    detail?.session?.id ||
    detail?.sessionId ||
    detail?.id ||
    ''
  );
}

function normalizeDateRange(fromDate, toDate) {
  const normalizedFrom = fromDate ? normalizeDateKey(fromDate) : '';
  const normalizedTo = toDate ? normalizeDateKey(toDate) : '';

  if (!normalizedFrom && !normalizedTo) {
    return { from: null, to: null };
  }

  const fromValue = normalizedFrom || normalizedTo;
  const toValue = normalizedTo || normalizedFrom;

  return {
    from: fromValue || null,
    to: toValue || null
  };
}

function isDateInRange(dateKey, range) {
  if (!range?.from && !range?.to) return true;
  const compare = dateKey;
  if (range?.from && compare < range.from) return false;
  if (range?.to && compare > range.to) return false;
  return true;
}

function getSessionDateKey(detail) {
  const session =
    detail?.session ||
    detail?.session_data ||
    detail?.session_info ||
    detail?.attendance_session ||
    {};
  const rawDate =
    session.date ||
    session.session_date ||
    session.start_date ||
    session.start ||
    session.schedule_date ||
    detail?.date ||
    detail?.session_date ||
    detail?.attendance_date;

  return normalizeDateKey(rawDate);
}

function normalizeDateKey(rawDate) {
  if (!rawDate) return '';
  if (typeof rawDate === 'string') {
    const trimmed = rawDate.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    const leadingDate = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
    if (leadingDate) {
      return leadingDate[1];
    }
  }

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return '';
  return formatDateToKey(parsed);
}

function formatDateToKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateKeyForTitle(dateKey) {
  if (!dateKey) return '';
  const match = String(dateKey).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(dateKey);
  const [, year, month, day] = match;
  return `${day}-${month}-${year}`;
}

function getTodayDateKey() {
  return formatDateToKey(new Date());
}

function cleanStudentName(name) {
  if (!name) return 'Unknown Student';
  return String(name).replace(/\s*\([^)]*\)\s*$/, '').trim() || 'Unknown Student';
}

function formatAttendancePercentage(value) {
  return value === null || value === undefined ? '-' : `${value}%`;
}

function looksLikePlainNumericId(value) {
  return /^\d+$/.test(String(value || '').trim());
}

function getDisplayStudentId(student) {
  const candidates = [
    student?.student_no,
    student?.student_code,
    student?.registration_no,
    student?.admission_no,
    student?.sid,
    student?.SID,
    student?.student_id,
    student?.id
  ];

  const firstNonNumeric = candidates.find(
    (candidate) => candidate && !looksLikePlainNumericId(candidate)
  );
  if (firstNonNumeric) return String(firstNonNumeric).trim();

  const firstAvailable = candidates.find((candidate) => candidate !== undefined && candidate !== null && candidate !== '');
  return firstAvailable ? String(firstAvailable).trim() : '';
}

function extractSubjectInfoFromViewDetails(viewDetailsList, fallbackSession) {
  const detailCandidates = (viewDetailsList || []).flatMap((detail) => {
    const firstStudent = Array.isArray(detail?.students) ? detail.students[0] : null;
    return [detail?.session || null, firstStudent].filter(Boolean);
  });

  const candidates = [...detailCandidates, fallbackSession].filter(Boolean);
  const source = pickBestSubjectInfoSource(candidates);

  if (!source) return null;

  const faculty =
    source.faculty_name ||
    source.faculty?.name ||
    source.faculty ||
    source.subject_faculty_name ||
    '';

  const department =
    source.department_name ||
    source.department?.name ||
    source.department ||
    source.subject_department_name ||
    '';

  const batchFromList = Array.isArray(source.batches)
    ? source.batches.map((b) => (typeof b === 'string' ? b : b?.name || '')).filter(Boolean).join(', ')
    : '';
  const batch =
    batchFromList ||
    source.batches ||
    source.batch_name ||
    source.batch ||
    source.class_name ||
    '';
  const tutorName =
    source.tutor_name ||
    source.teacher_name ||
    source.instructor_name ||
    source.employee_name ||
    source.tutor?.name ||
    source.teacher?.name ||
    source.instructor?.name ||
    '';

  const rawInfo = {
    faculty: faculty ? String(faculty).trim() : '',
    department: department ? String(department).trim() : '',
    batch: batch ? String(batch).trim() : '',
    tutorName: tutorName ? String(tutorName).trim() : ''
  };

  return enrichSubjectInfoFromBatch(rawInfo);
}

function pickBestSubjectInfoSource(candidates) {
  let best = null;
  let bestScore = -1;

  candidates.forEach((candidate) => {
    const score = [
      candidate?.faculty_name || candidate?.faculty?.name || candidate?.faculty || candidate?.subject_faculty_name,
      candidate?.department_name || candidate?.department?.name || candidate?.department || candidate?.subject_department_name,
      candidate?.batch_name || candidate?.batch || candidate?.class_name || candidate?.batches
    ].filter(Boolean).length;

    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  });

  return best;
}

function enrichSubjectInfoFromBatch(subjectInfo) {
  const info = {
    faculty: subjectInfo?.faculty || '',
    department: subjectInfo?.department || '',
    batch: subjectInfo?.batch || '',
    tutorName: subjectInfo?.tutorName || ''
  };

  const primaryBatchCode = String(info.batch)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)[0];

  if (!primaryBatchCode) return info;

  const inferred = inferFromBatchCode(primaryBatchCode);
  if (!info.department && inferred.department) {
    info.department = inferred.department;
  }
  if (!info.faculty && inferred.faculty) {
    info.faculty = inferred.faculty;
  }

  return info;
}

function inferFromBatchCode(batchCode) {
  const normalized = String(batchCode || '').trim().toUpperCase();
  const majorCode = normalized.includes('-') ? normalized.split('-').pop() : '';

  const departmentByCode = {
    MLS: 'Medical Laboratory Science',
    NRS: 'Nursing',
    MW: 'Midwifery',
    MED: 'Medicine',
    PH: 'Public Health',
    DEN: 'Dentistry',
    IT: 'Information Technology',
    CS: 'Computer Science',
    ACC: 'Accounting',
    BUS: 'Business Administration',
    EDU: 'Education'
  };

  const healthDepartmentCodes = new Set(['MLS', 'NRS', 'MW', 'MED', 'PH', 'DEN']);
  const techDepartmentCodes = new Set(['IT', 'CS']);

  const department = departmentByCode[majorCode] || '';

  let faculty = '';
  if (normalized.startsWith('HS') || healthDepartmentCodes.has(majorCode)) {
    faculty = 'Faculty of Health Science';
  } else if (normalized.startsWith('CS') || techDepartmentCodes.has(majorCode)) {
    faculty = 'Faculty of Computer Science & IT';
  } else if (normalized.startsWith('ED') || majorCode === 'EDU') {
    faculty = 'Faculty of Education';
  } else if (normalized.startsWith('MD')) {
    faculty = 'Faculty of Medicine';
  } else if (normalized.startsWith('BA') || ['ACC', 'BUS'].includes(majorCode)) {
    faculty = 'Faculty of Business Administration';
  }

  return { faculty, department };
}

function PercentageComboDropdown({
  label,
  placeholder,
  value,
  onChange,
  options,
  wrapperClassName = ''
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleInputChange = (event) => {
    onChange(sanitizePercentageComboInput(event.target.value));
    if (!isOpen) setIsOpen(true);
  };

  const selectOption = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  return (
    <div className={wrapperClassName} ref={containerRef}>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          className="w-full h-[42px] pr-10 pl-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="absolute inset-y-0 right-0 px-3 text-gray-500 hover:text-gray-700"
          aria-label={`Toggle ${label} options`}
        >
          <MdExpandMore className="text-xl" />
        </button>

        {isOpen && (
          <div className="absolute z-40 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
            {options.map((option) => {
              const isSelected = value === option;
              return (
                <button
                  key={`${label}-${option}`}
                  type="button"
                  onClick={() => selectOption(option)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left transition-colors ${
                    isSelected
                      ? 'bg-blue-50 text-blue-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{option}%</span>
                  {isSelected && <MdCheck className="text-base" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function parsePercentageSelection(value) {
  if (value === '') return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return Math.max(0, Math.min(100, numericValue));
}

function sanitizePercentageComboInput(value) {
  if (value === '') return '';
  const digitsOnly = String(value).replace(/[^\d]/g, '');
  if (digitsOnly === '') return '';
  const numericValue = Number(digitsOnly);
  if (!Number.isFinite(numericValue)) return '';
  return String(Math.max(0, Math.min(100, numericValue)));
}

function sanitizeFileName(value) {
  return String(value || 'subject')
    .trim()
    .replace(/[^\w\-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 60);
}

function formatSessionDate(session) {
  const rawDate = session.date || session.session_date || session.start_date;
  if (!rawDate) return 'Unknown date';
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) {
    return String(rawDate);
  }
  return parsed.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

function formatSessionDay(session) {
  const rawDate = session.date || session.session_date || session.start_date;
  if (!rawDate) return 'Unknown day';
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return 'Unknown day';
  return parsed.toLocaleDateString('en-GB', { weekday: 'long' });
}

function formatSessionTime(session) {
  const start = session.start_time || session.time_start || session.start || session.start_at;
  const end = session.end_time || session.time_end || session.end || session.end_at;

  if (!start && !end) return 'Time not available';

  const formatTime = (value) => {
    if (!value) return '';
    if (typeof value === 'string' && value.includes(':')) {
      const [hours, minutes] = value.split(':');
      const parsed = new Date();
      parsed.setHours(Number(hours));
      parsed.setMinutes(Number(minutes));
      parsed.setSeconds(0);
      return parsed.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formattedStart = formatTime(start);
  const formattedEnd = formatTime(end);
  if (formattedStart && formattedEnd) return `${formattedStart} – ${formattedEnd}`;
  return formattedStart || formattedEnd || 'Time not available';
}

function formatSessionPeriodTime(session) {
  const periodLabel = extractPeriodLabel(session);
  const start = session.start_time || session.time_start || session.start || session.start_at;
  const end = session.end_time || session.time_end || session.end || session.end_at;
  const dayLabel = normalizeDayLabel(
    session.day_name || session.day || session.weekday || session.session_day
  );

  const timeRange = formatTimeRange(start, end);
  const pieces = [];

  if (periodLabel) {
    pieces.push(`Period ${periodLabel}`);
  } else {
    pieces.push('Period');
  }

  if (timeRange) {
    pieces.push(`(${timeRange})`);
  }

  let result = pieces.join(' ');
  if (dayLabel) {
    result += `: ${dayLabel}`;
  }

  return result.trim() || 'Time not available';
}

function formatTimeRange(start, end) {
  const formattedStart = formatClock(start);
  const formattedEnd = formatClock(end);

  if (formattedStart && formattedEnd) return `${formattedStart} - ${formattedEnd}`;
  return formattedStart || formattedEnd || '';
}

function formatClock(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const isoCandidate = value.includes('T');
    const timeMatch = value.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      return formatTo12Hour(Number(timeMatch[1]), Number(timeMatch[2]));
    }
    if (isoCandidate) {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      }
    }
    return '';
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatTo12Hour(hours, minutes) {
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return '';
  const period = hours >= 12 ? 'PM' : 'AM';
  const normalizedHour = hours % 12 || 12;
  const paddedMinutes = String(minutes).padStart(2, '0');
  return `${normalizedHour}:${paddedMinutes} ${period}`;
}

function normalizeDayLabel(value) {
  if (value === undefined || value === null || value === '') return '';
  const dayMap = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  if (typeof value === 'number' && Number.isFinite(value)) {
    return dayMap[value] || '';
  }
  const raw = String(value).trim();
  if (raw === '') return '';
  const numeric = Number(raw);
  if (Number.isFinite(numeric)) {
    if (numeric >= 0 && numeric <= 6) return dayMap[numeric] || '';
    if (numeric >= 1 && numeric <= 7) return dayMap[numeric % 7] || '';
  }
  const normalized = raw.toLowerCase();
  const match = dayMap.find((day) => day.toLowerCase().startsWith(normalized.slice(0, 3)));
  return match || raw.charAt(0).toUpperCase() + raw.slice(1);
}

function extractPeriodLabel(session) {
  const candidate =
    session.period ||
    session.period_number ||
    session.period_no ||
    session.period_name ||
    session.period_id?.name;

  if (candidate === undefined || candidate === null || candidate === '') return '';
  const raw = String(candidate);
  const numberMatch = raw.match(/\d+/);
  return numberMatch ? numberMatch[0] : raw.replace(/period/i, '').trim();
}

function getSessionBatchName(session) {
  const batchList = [
    session.batch_name,
    session.batch_code,
    session.batch,
    session.class_name,
    session.class_code,
    session.class,
    session.subject_batch_name,
    session.batch?.name,
    session.class?.name
  ];

  const arrayCandidates = [];
  if (Array.isArray(session.batches)) arrayCandidates.push(session.batches);
  if (Array.isArray(session.batch_ids)) arrayCandidates.push(session.batch_ids);

  const arrayName = arrayCandidates
    .flat()
    .map((batch) => (typeof batch === 'string' ? batch : batch?.name || ''))
    .filter(Boolean)
    .join(', ');

  const firstValue = batchList.find((value) => value !== undefined && value !== null && value !== '');
  if (arrayName) return arrayName;
  return firstValue ? String(firstValue) : '';
}

function getSessionLecturer(session) {
  return (
    session.tutor_name ||
    session.teacher_name ||
    session.lecturer_name ||
    session.instructor_name ||
    session.employee_name ||
    session.tutor?.name ||
    session.teacher?.name ||
    session.instructor?.name ||
    session.lecturer?.name ||
    ''
  );
}

function capitalizeLabel(value) {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function drawUniversityPdfHeader(doc, pageWidth, reportTitle = 'Attendance Report') {
  const marginX = 14;
  const headerTop = 11;
  const logoWidth = 18;
  const logoHeight = 18;
  const contentGap = 3.8;

  // Group is centered: logo (left) + text block (right)
  const textBlockWidth = 96;
  const groupWidth = logoWidth + contentGap + textBlockWidth;
  const groupStartX = (pageWidth - groupWidth) / 2;
  const logoX = groupStartX;
  const logoY = headerTop;
  const textX = logoX + logoWidth + contentGap;

  try {
    doc.addImage(universityLogo, 'PNG', logoX, logoY, logoWidth, logoHeight, 'university-logo', 'FAST');
  } catch (err) {
    // Keep header rendering even if logo parsing fails
    console.warn('Could not render university logo in PDF:', err);
  }

  doc.setTextColor(17, 24, 39);
  doc.setFontSize(14.5);
  doc.setFont(undefined, 'bold');
  doc.text('BENADIR UNIVERSITY', textX, headerTop + 5.8);

  doc.setTextColor(75, 85, 99);
  doc.setFontSize(9.6);
  doc.setFont(undefined, 'normal');
  doc.text('CULTIVATING HUMAN TALENTS', textX, headerTop + 10.4);

  doc.setTextColor(31, 41, 55);
  doc.setFontSize(12.2);
  doc.setFont(undefined, 'bold');
  doc.text(reportTitle, textX, headerTop + 15.2);

  const dividerY = headerTop + logoHeight + 2.8;
  doc.setDrawColor(209, 213, 219);
  doc.setLineWidth(0.35);
  doc.line(marginX, dividerY, pageWidth - marginX, dividerY);

  return dividerY;
}

export default TutorAttendanceReports;
